<script setup lang="ts">
import PlanCard from '@/components/Plan/PlanCard.vue';
import { useUserStore } from '@/stores/useUserStore';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const props = defineProps<{
  visible: boolean;
}>();
const router = useRouter();

const emit = defineEmits<{
  'update:visible': [value: boolean];
}>();

const { t } = useI18n();
const userStore = useUserStore();

const localVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value),
});

const previousPlan = computed(() => {
  return userStore.previousSubscription?.plan?.name || t('subscription.changed.previous_plan');
});

const newPlanName = computed(() => {
  return userStore.newSubscription?.plan?.name || t('subscription.changed.new_plan');
});

const newPlan = computed(() => {
  return userStore.newSubscription?.plan || null;
});

const handleAcknowledge = async () => {
  await userStore.acknowledgeSubscriptionChange();
  localVisible.value = false;
};

const goToSubscription = async () => {
  await userStore.acknowledgeSubscriptionChange();
  await userStore.acceptTrialPopup();
  localVisible.value = false;
  router.push({ name: 'subscription-plans' });
};
</script>

<template>
  <Dialog v-model:visible="localVisible" modal :style="{ width: '90vw', maxWidth: '450px' }" :closable="false">
    <template #header>
      <div class="flex items-center gap-2">
        <i class="pi pi-check-circle text-xl text-green-500"></i>
        <span class="font-semibold">{{ $t('subscription.changed.title') }}</span>
      </div>
    </template>

    <div class="space-y-4">
      <div v-if="previousPlan && newPlanName" class="text-center">
        <div class="mb-4 flex items-center justify-center gap-4">
          <div class="text-center">
            <div class="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-600">
              {{ previousPlan }}
            </div>
            <span class="mt-1 block text-xs text-gray-500">{{ $t('subscription.changed.previous_plan') }}</span>
          </div>
          <i class="pi pi-arrow-right text-gray-400"></i>
          <div class="text-center">
            <div class="rounded-lg bg-green-100 px-3 py-2 text-sm font-medium text-green-700">
              {{ newPlanName }}
            </div>
            <span class="mt-1 block text-xs text-green-600">{{ $t('subscription.changed.new_plan') }}</span>
          </div>
        </div>
      </div>

      <div class="rounded-lg bg-green-50 p-3">
        <h4 class="mb-2 font-medium text-green-800">{{ $t('subscription.changed.new_plan') }} : {{ newPlanName }}</h4>
        <PlanCard v-if="newPlan" :plan="newPlan" show-features class="w-full" hide-price />
      </div>
    </div>

    <template #footer>
      <div class="flex w-full flex-row items-center justify-between gap-3">
        <Button :label="t('subscription.seeSubscriptions')" outlined @click="goToSubscription" />
        <Button :label="t('common.ok')" class="bg-green-500 px-6 text-white hover:bg-green-600" @click="handleAcknowledge" />
      </div>
    </template>
  </Dialog>
</template>
